import { useMemo } from 'react'
import { useQueries } from 'react-query'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import { fetchContactById } from '@features/contacts/query-api'
import type { Account } from '@modules-deprecated/app/accounts/types'
import { fetchContacts } from '@modules-deprecated/app/contacts/query-api'
import type { FetchContactsQueryParams } from '@modules-deprecated/app/contacts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import type { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { QueryKeys } from '../../../enums/queryKeys'
import { APIError } from '../../../utils'
import type { ReviewEntryItem } from '../types/reviewEntryItem'
import { getMatchedSupplierContactId } from '../utils/getMatchedSupplierContactId'
import { getReviewEntryContactId } from '../utils/getReviewEntryContactId'
import { getReviewEntryId } from '../utils/getReviewEntryId'
import { getReviewEntryValidationFlags, ReviewEntryValidationFlags } from '../utils/getReviewEntryValidationFlags'
import { hasThirdPartyQuestion } from '../utils/reviewEntryThirdPartyQuestion'
import { useFetchPurchaseTaxRates } from './useFetchPurchaseTaxRates'

export interface ReviewEntryValidationState extends ReviewEntryValidationFlags {
  entryId: string
  item: ReviewEntryItem
  supplierId: string | null
}

interface UseReviewEntriesValidationProps {
  items: ReviewEntryItem[]
  itemsWithDrafts: ReviewEntryItem[]
}

interface UseReviewEntriesValidationResult {
  accounts: Account[]
  purchaseTaxRates: TaxRate[]
  validationStates: ReviewEntryValidationState[]
}

export const useReviewEntriesValidation = ({
  items,
  itemsWithDrafts,
}: UseReviewEntriesValidationProps): UseReviewEntriesValidationResult => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id
  const { purchaseTaxRates } = useFetchPurchaseTaxRates()
  const { accounts } = useFetchAccounts()

  const fallbackSupplierNames = useMemo(() => {
    const names = itemsWithDrafts
      .filter(
        (item) =>
          getReviewEntryContactId(item.processingDecision) === null && !hasThirdPartyQuestion(item.questions ?? []),
      )
      .map((item) => item.supplierName?.trim())
      .filter((name): name is string => Boolean(name))

    return [...new Set(names)]
  }, [itemsWithDrafts])

  const automatchedContactIds = useMemo(() => {
    const ids = new Set<string>()

    for (const item of itemsWithDrafts) {
      const id = getReviewEntryContactId(item.processingDecision)

      if (id) {
        ids.add(id)
      }
    }

    return [...ids]
  }, [itemsWithDrafts])

  const contactByIdQueries = useQueries(
    automatchedContactIds.map((contactId) => ({
      enabled: true,
      queryFn: async () => {
        try {
          return await fetchContactById(contactId)
        } catch (error) {
          // a 404 here just means the AI-matched contact was deleted — mute Datadog tracking
          // so the expected miss does not surface as an app error
          if (error instanceof APIError && error.statusCode === 404) {
            error.silent = true
          }

          throw error
        }
      },
      queryKey: [QueryKeys.Contact, contactId],
      retry: (failureCount: number, error: unknown) =>
        failureCount < 3 && (error as APIError | undefined)?.statusCode !== 404,
    })),
  )

  const contactByIdResolvedSignature = contactByIdQueries.map((query) => (query.data ? '1' : '0')).join('')

  const resolvedAutomatchedContactIds = useMemo(() => {
    const resolved = new Set<string>()

    for (const [index, contactId] of automatchedContactIds.entries()) {
      if (contactByIdQueries[index]?.data) {
        resolved.add(contactId)
      }
    }

    return resolved
    // query data is what matters here; `contactByIdQueries` is a fresh array every render,
    // so we depend on the derived signature above rather than the queries themselves
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automatchedContactIds, contactByIdResolvedSignature])

  const contactQueries = useQueries(
    fallbackSupplierNames.map((supplierName) => {
      const queryProps: FetchContactsQueryParams | undefined = organizationId
        ? {
            isSupplier: true,
            organizationId,
            page: 1,
            pageSize: 100,
            q: supplierName,
          }
        : undefined

      return {
        enabled: queryProps !== undefined,
        queryFn: () => fetchContacts(queryProps as FetchContactsQueryParams),
        queryKey: [QueryKeys.Contacts, queryProps],
      }
    }),
  )

  const contactQueriesSignature = contactQueries
    .map((query) => query.data?.contacts?.map((contact) => contact.id).join('|') ?? '')
    .join(';')

  const supplierIdByName = useMemo(() => {
    const supplierIds = new Map<string, string | null>()

    for (const [nameIndex, supplierName] of fallbackSupplierNames.entries()) {
      supplierIds.set(
        supplierName,
        getMatchedSupplierContactId({ contacts: contactQueries[nameIndex]?.data?.contacts, supplierName }),
      )
    }

    return supplierIds
    // `contactQueries` is a new array every render; the derived signature above tracks
    // when the query results actually change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackSupplierNames, contactQueriesSignature])

  const validationStates = useMemo(
    () =>
      itemsWithDrafts.map((itemWithDraft, itemIndex) => {
        const sourceItem = items[itemIndex] ?? itemWithDraft
        const automatchedContactId = getReviewEntryContactId(itemWithDraft.processingDecision)

        let supplierId: string | null
        if (automatchedContactId) {
          supplierId = resolvedAutomatchedContactIds.has(automatchedContactId) ? automatchedContactId : null
        } else if (hasThirdPartyQuestion(itemWithDraft.questions ?? [])) {
          supplierId = null
        } else {
          supplierId = supplierIdByName.get(itemWithDraft.supplierName?.trim() ?? '') ?? null
        }

        return {
          ...getReviewEntryValidationFlags({ item: itemWithDraft, supplierId, vatRates: purchaseTaxRates }),
          entryId: getReviewEntryId(sourceItem, itemIndex),
          item: sourceItem,
          supplierId,
        }
      }),
    [items, itemsWithDrafts, purchaseTaxRates, resolvedAutomatchedContactIds, supplierIdByName],
  )

  return { accounts, purchaseTaxRates, validationStates }
}
